import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import {
  DataGrid,
  GridEditModes,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import { Box } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { useSnackbar } from "notistack"
import i18n from "i18n"
import DataGridLocaleText from "common/DataGridLocaleText"
import ConfirmDeleteModal from "./ConfirmDeleteModal"
import CanNotDeleteModal from "./CanNotDeleteModal"
import { useDynamicElements } from "hooks/DynamicElements/useDynamicElements"
import { useColumnWidths } from "hooks/DynamicElements/useColumnWidths"
import { useColumns } from "hooks/DynamicElements/useColumns"
import { useDeleteElement } from "hooks/DynamicElements/useDeleteElement"
import { useAutoSave } from "hooks/DynamicElements/useAutoSave"
import { isNil } from "lodash"

const theme = createTheme({
  palette: {
    primary: { main: "#555555" },
    secondary: { main: "#FFFFFF" },
  },
})

const DynamicElementToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarQuickFilter />
  </GridToolbarContainer>
)

const DynamicElementList = ({
  organizationId,
  children,
  apiRef,
  showActions = true,
}) => {
  const { dynamicElements, dispatches } = useDynamicElements()
  const { enqueueSnackbar } = useSnackbar()
  const { columnWidths, handleCellEditStart, handleCellEditStop } =
    useColumnWidths()

  const { autoSave, isSaving, isSuccess, shouldShowButton } =
    useAutoSave(organizationId)

  const columns = useColumns(
    apiRef,
    columnWidths,
    autoSave,
    isSaving,
    isSuccess,
    shouldShowButton,
  )

  const rows = useMemo(
    () => dynamicElements.map(e => ({ ...e, organizationId })),
    [dynamicElements, organizationId],
  )

  const toolbarSlots = useMemo(() => ({ toolbar: DynamicElementToolbar }), [])

  const columnVisibilityModel = useMemo(
    () => ({ itemId: false, actions: showActions }),
    [showActions],
  )

  const localeText = useMemo(() => DataGridLocaleText(), [])

  const handleRowUpdate = (newRow, oldRow) => {
    const hasChanges = Object.keys(newRow).some(
      key => newRow[key] !== oldRow[key],
    )

    if (hasChanges) {
      autoSave(newRow)
    }

    return newRow
  }

  const handleRowUpdateError = error => {
    enqueueSnackbar(i18n.t("snackbar.unSuccessfullySaved"), {
      variant: "error",
    })
    console.error("Error updating row:", error)
  }

  useEffect(() => {
    dispatches.getDynamicElements(organizationId)
  }, [])

  const {
    handleCancelDeleteModal,
    handleConfirm,
    deleteElement,
    handleCloseCanNotDeleteModal,
  } = useDeleteElement(organizationId)

  return (
    <div style={{ width: "100%" }}>
      <ThemeProvider theme={theme}>
        <Box>{children}</Box>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ height: 450 }}>
            <DataGrid
              slots={toolbarSlots}
              {...(apiRef
                ? { apiRef, checkboxSelection: dynamicElements.length > 0 }
                : {})}
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              editMode={apiRef ? GridEditModes.None : GridEditModes.Cell}
              hideFooterPagination
              hideFooter
              sx={{
                overflow: "clip",
                "& .MuiDataGrid-row": {
                  "& .delete-action-button": {
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                  },
                  "& .edit-action-button": {
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                  },
                  "&:hover .delete-action-button": {
                    opacity: 1,
                  },
                  // Always show edit button when it's in saving or success state
                  '& .edit-action-button[data-saving="true"]': {
                    opacity: "1 !important",
                  },
                },
              }}
              getRowId={row => row.itemId}
              onCellEditStart={!apiRef ? handleCellEditStart : undefined}
              onCellEditStop={!apiRef ? handleCellEditStop : undefined}
              processRowUpdate={!apiRef ? handleRowUpdate : undefined}
              onProcessRowUpdateError={
                !apiRef ? handleRowUpdateError : undefined
              }
              localeText={localeText}
            />
          </Box>
        </Box>

        <ConfirmDeleteModal
          open={deleteElement.openConfirmModal}
          onClose={handleCancelDeleteModal}
          onConfirm={handleConfirm}
          elementToDelete={deleteElement.elementToDelete}
        />

        <CanNotDeleteModal
          open={!isNil(deleteElement.partsRelated)}
          onClose={handleCloseCanNotDeleteModal}
          elementToDelete={deleteElement.elementToDelete}
          partsRelated={deleteElement.partsRelated}
        />
      </ThemeProvider>
    </div>
  )
}

DynamicElementList.propTypes = {
  organizationId: PropTypes.string.isRequired,
  children: PropTypes.node,
  apiRef: PropTypes.shape({
    current: PropTypes.object,
  }),
  showActions: PropTypes.bool,
}

export default DynamicElementList
